import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { FaHistory } from "react-icons/fa";
import { toast } from "react-toastify";

import { useSearchParams } from "react-router-dom";
import {
  GET_CUSTOM_TIP_HISTORY,
  REFUND_CUSTOM_TIP_REDEEM,
} from "../../../queries/mutation/rein-tips/queries";
import { ReinCustomTip } from "../../../types/ReinCustomTip";
import LoadingPage from "../../LoadingPage";

type Props = {
  isBroadcaster: boolean;
};

const formatDateTime = (epochMs: string) => {
  const date = new Date(Number(epochMs));
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const CustomTipHistory = ({ isBroadcaster }: Props) => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [pendingRefund, setPendingRefund] = useState<ReinCustomTip | null>(
    null,
  );

  const {
    data: customTipHistoryData,
    refetch,
    loading,
  } = useQuery(GET_CUSTOM_TIP_HISTORY, {
    fetchPolicy: "cache-and-network",
    variables: { userId: isBroadcaster ? id : null },
  });

  const [refundRedeem, { loading: refunding }] = useMutation(
    REFUND_CUSTOM_TIP_REDEEM,
    {
      onCompleted: () => {
        toast.success("Refunded - the crimcoins are back in their balance");
        setPendingRefund(null);
        // Picks up the new credit entry, not just the refunded mark.
        refetch();
      },
      onError: (e) => {
        toast.error(e.message);
        setPendingRefund(null);
        refetch();
      },
    },
  );

  if (loading || !customTipHistoryData?.getCustomTipHistory) {
    return <LoadingPage />;
  }

  const { customTips, username } = customTipHistoryData.getCustomTipHistory;
  const history: ReinCustomTip[] = customTips ?? [];
  return (
    <div className="bg-base-200 h-full flex flex-col gap-2 items-center p-2 animate-fade-in">
      <div className="card bg-base-100 w-full sm:w-6/12 overflow-auto flex">
        <div className="card-body p-2 sm:p-4 overflow-auto flex flex-col">
          <div className="card-title justify-between flex flex-row">
            <div>
              <div className="inline text-primary">{username}</div> Offline Tips
              History
            </div>
            <div className="text-primary">
              <FaHistory size={24} />
            </div>
          </div>
          <div className="divider divider-primary m-0" />
          <div>
            Total Offline Tips:
            <div className="text-primary inline">{history.length}</div>
          </div>

          <div className="overflow-y-auto gap-2 flex flex-grow flex-col bg-base-300 p-2 rounded-box">
            {history.map((p, index) => {
              const amount = Number(p.amount);
              // Credits and free redeems have nothing to give back, and a
              // removal is corrected with another adjustment, not a refund.
              const canRefund =
                isBroadcaster &&
                amount < 0 &&
                p.type !== "adjustment" &&
                !p.refundedAt;

              return (
                <div
                  key={p.id ?? index}
                  className="border-2 border-primary rounded-box p-2 shadow-lg hover:bg-base-100"
                >
                  <div className="flex flex-row justify-between">
                    <div
                      className={`${
                        amount < 0 ? "text-error" : "text-success"
                      }`}
                    >
                      {amount.toLocaleString()} Crim Coins
                    </div>
                    <div className="text-success">
                      {p.type.toLocaleUpperCase()}
                    </div>
                  </div>
                  {p.description && (
                    <div className="text-sm text-base-content/70">
                      {p.description}
                    </div>
                  )}
                  <div className="flex flex-row justify-between">
                    <div className="text-sm">
                      {formatDateTime(p.createdAt)}
                    </div>
                    {p.refundedAt ? (
                      <span className="badge badge-info badge-sm">
                        Refunded
                        {p.refundedBy === "system"
                          ? " automatically"
                          : p.refundedBy
                            ? ` by ${p.refundedBy}`
                            : ""}
                      </span>
                    ) : (
                      canRefund && (
                        <button
                          className="btn btn-xs btn-outline btn-error"
                          onClick={() => setPendingRefund(p)}
                        >
                          Refund
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {pendingRefund && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              Refund {Math.abs(Number(pendingRefund.amount)).toLocaleString()}{" "}
              Crim Coins to {username}?
            </h3>
            <p className="py-2 text-sm">
              {pendingRefund.description ??
                pendingRefund.type.toLocaleUpperCase()}{" "}
              on {formatDateTime(pendingRefund.createdAt)}
            </p>
            <p className="text-sm text-base-content/70">
              This gives back their crimcoins but doesn't undo what the redeem
              did - a deny still counts in the war. They'll get a Discord DM
              that it was refunded.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPendingRefund(null)}
                disabled={refunding}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                disabled={refunding}
                onClick={() =>
                  refundRedeem({
                    variables: { customTipId: pendingRefund.id },
                  })
                }
              >
                {refunding ? "Refunding..." : "Refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CustomTipHistory;
